/**
 * Icons.
 *
 * Every glyph is an SVG exported from the Figma `icon/All` set and committed under
 * `./icons/`. Nothing here is drawn by hand: the only edit made to an exported file is
 * swapping its baked-in colour for `currentColor`, so one asset serves every state.
 *
 * The files are imported with Vite's `?raw` and inlined, rather than used as an `<img>`
 * source, because an `<img>` cannot inherit `currentColor`.
 *
 * To add an icon: export it from Figma at its native size, drop the file in `./icons/`,
 * replace `stroke="…"` or `fill="…"` with `currentColor`, and add it to `ICONS` below.
 */
import arrowCircleRight from './icons/arrow-circle-right.svg?raw';
import arrowCircleUpRight from './icons/arrow-circle-up-right.svg?raw';
import arrowRight from './icons/arrow-right.svg?raw';
import arrowRight24 from './icons/arrow-right-24.svg?raw';
import checkCircle from './icons/check-circle.svg?raw';
import chevronCircleRight from './icons/chevron-circle-right.svg?raw';
import chevronDown from './icons/chevron-down.svg?raw';
import close from './icons/close.svg?raw';
import closeCircle from './icons/close-circle.svg?raw';
import facebook from './icons/facebook.svg?raw';
import fileDownload from './icons/file-download.svg?raw';
import flagCheck from './icons/flag-check.svg?raw';
import globe from './icons/globe.svg?raw';
import linkedin from './icons/linkedin.svg?raw';
import mail from './icons/mail.svg?raw';
import phone from './icons/phone.svg?raw';
import playCircle from './icons/play-circle.svg?raw';
import plusCircle from './icons/plus-circle.svg?raw';
import verified from './icons/verified.svg?raw';

export const ICONS = {
  'arrow-right': arrowRight,
  // The mobile menu's open/closed marker (Figma 6583:23310), rotated to point down or up.
  // Its own 24px export: arrow-right scaled from 18 would draw a 2px line where this is 1.6.
  'arrow-right-24': arrowRight24,
  'arrow-circle-right': arrowCircleRight,
  // The diagonal "opens elsewhere" arrow in its disc (Figma 3474:39528). The arrow is a hole;
  // its baked `#A0A0A0` is grey-700, swapped for currentColor.
  'arrow-circle-up-right': arrowCircleUpRight,
  // Exported from the feature list over the MY-WCP photo (Figma 6377:88789). Its baked
  // `#30C566` is green-600; swapped for currentColor, so the caller picks the colour.
  'check-circle': checkCircle,
  // The text slider's previous/next buttons (Figma 6377:88433). The chevron is a hole
  // in the disc, so the panel behind it shows through; one glyph, mirrored for previous.
  'chevron-circle-right': chevronCircleRight,
  'chevron-down': chevronDown,
  // The columns accordion's two states (Figma 6377:87981 open, 6377:87992 closed). Both
  // are discs with the glyph cut out, so the panel's own colour draws the cross and the plus.
  'close-circle': closeCircle,
  // The mobile menu's close button (Figma 6583:23304) and the language button beside it
  // (6583:23302). Both were exported black; the button draws the frame.
  close,
  // The footer's social row (Figma 3883:70490). Both were exported white; currentColor
  // lets them dim with the link around them.
  facebook,
  // The landing page's download cards (Figma 6440:36102). Its baked `#0948f8` is
  // blue-500; currentColor lets the card decide, which the dark section background needs.
  'file-download': fileDownload,
  // The visa picker's empty panel, in its round badge (Figma 6377:87660). Its baked
  // `#0948F8` is blue-500; currentColor lets the badge set it.
  'flag-check': flagCheck,
  globe,
  linkedin,
  // The contact card's own rows (Figma 6440:36170 and 6440:36175). `mail` is a stroked
  // glyph where every other icon here is filled, so it takes `stroke="currentColor"`;
  // both were exported at the size the design draws them, 24 and 20.
  mail,
  phone,
  // The play button the design draws over a video tile (Figma 1225:5741). Its baked
  // `#000F76` is swapped for currentColor: it is white over a photograph.
  'play-circle': playCircle,
  'plus-circle': plusCircle,
  verified
} as const;

export type IconName = keyof typeof ICONS;

export function Icon({ name, size = 18, className }: { name: IconName; size?: number; className?: string }) {
  return (
    <span
      aria-hidden
      className={`inline-block shrink-0 [&>svg]:h-full [&>svg]:w-full ${className ?? ''}`}
      style={{ width: size, height: size }}
      // Build-time constant from a committed file, not user input.
      dangerouslySetInnerHTML={{ __html: ICONS[name] }}
    />
  );
}
